import type { EpisodeProgress } from '@/domain/progress'
import type { ProgressRepository } from '@/repositories/ProgressRepository'
import { mergeProgress } from '@/domain/progressMerge'

const STORAGE_KEY = 'cybertales.progress'

function readAll(): EpisodeProgress[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as EpisodeProgress[]) : []
  } catch {
    return []
  }
}

function writeAll(items: EpisodeProgress[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage unavailable — progress simply won't persist across reloads.
  }
}

// Zero-cost implementation of ProgressRepository, backed by localStorage
// (Document 11 §4). CosmosResultsRepository (P5) implements the same
// interface against Cosmos DB via the production Episode/Quiz Service.
export class LocalProgressRepository implements ProgressRepository {
  async getForUser(userId: string): Promise<EpisodeProgress[]> {
    return readAll().filter((p) => p.userId === userId)
  }

  async getForEpisode(userId: string, episodeId: string): Promise<EpisodeProgress | null> {
    return readAll().find((p) => p.userId === userId && p.episodeId === episodeId) ?? null
  }

  async upsert(progress: EpisodeProgress): Promise<void> {
    const all = readAll()
    const idx = all.findIndex((p) => p.userId === progress.userId && p.episodeId === progress.episodeId)
    if (idx >= 0) {
      // P1.1 fix: merge rather than overwrite, so completed:true can never
      // regress to false (e.g. replaying an already-completed episode and
      // leaving before reaching the completion threshold again).
      all[idx] = mergeProgress(all[idx], progress)
    } else {
      all.push(progress)
    }
    writeAll(all)
  }
}
